/**
 * Phase 2/3 shared component — record an externally-signed instrument.
 *
 * Phase A multi-file (2026-11): accepts one or more PDFs per call.
 * The server allows up to 25 MiB per file; the modal enforces a soft
 * cap of 10 files per bundle.
 *
 * Used by:
 *   • Phase 2: ContractDetailPage → external-basis Contract Record
 *                                   (two-click; parent calls mark-effective
 *                                   separately).
 *   • Phase 3: AppointmentPanel   → LRS Paid Services Appointment
 *                                   (one-click; the backend endpoint
 *                                   auto-marks-effective).
 *
 * Props:
 *   open             boolean      — modal visibility
 *   onClose          () => void
 *   endpointUrl      string       — the admin POST endpoint
 *   onSuccessData    (data) => void — receives the server JSON
 *   submitLabel      string       — e.g. "Record Execution" | "Execute Appointment"
 *   helpText         string       — hint shown under the title
 *   testIdPrefix     string       — e.g. "ws6-rec-ext-exec" | "ws5-apt-exec-upload"
 *
 * The modal manages its own `op_id` (uuid4, frozen for the modal's
 * lifetime so retries = idempotent replay).  All validation is
 * client-side pre-submit; server-side errors are surfaced inline.
 */
import React, { useRef, useState } from "react";
import axios from "axios";

export const EXT_METHOD_LABELS = {
    zoho_sign: "Zoho Sign",
    docusign: "DocuSign",
    hand_signed: "Hand signed",
    other_e_sign: "Other e-sign platform",
    other: "Other",
};

export const MAX_FILE_BYTES = 25 * 1024 * 1024;         // 25 MiB per file
export const MAX_FILES_SOFT_CAP = 10;                   // soft cap per bundle

export function uuid4() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "op-" + Date.now().toString(36) + "-" +
           Math.random().toString(36).slice(2, 10) + "-" +
           Math.random().toString(36).slice(2, 10);
}

function _emptySigner() {
    return {
        signer_name: "",
        signer_email: "",
        signer_role: "",
        signed_on: new Date().toISOString().slice(0, 10),
        signature_method: "zoho_sign",
        external_envelope_ref: "",
        notes: "",
    };
}

function _fmtSize(bytes) {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function _validateFile(f) {
    const nameLower = (f.name || "").toLowerCase();
    const typeOk = f.type === "application/pdf" || nameLower.endsWith(".pdf");
    if (!typeOk) return "Must be a PDF.";
    if (f.size > MAX_FILE_BYTES) {
        return `Exceeds 25 MiB (file is ${_fmtSize(f.size)}).`;
    }
    return "";
}

export default function RecordExternalExecutionModal({
    open, onClose, endpointUrl, onSuccessData,
    submitLabel = "Record Execution",
    helpText = ("Attach one or more externally-signed PDFs and capture "
                + "structured signer evidence."),
    testIdPrefix = "rec-ext-exec",
}) {
    const [opId] = useState(() => uuid4());
    const [pdfFiles, setPdfFiles] = useState([]);
    const [signers, setSigners] = useState([_emptySigner()]);
    const [methodSummary, setMethodSummary] = useState("");
    const [externalRef, setExternalRef] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const fileInputRef = useRef(null);

    if (!open) return null;

    const updateSigner = (idx, patch) =>
        setSigners(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
    const addSigner = () => setSigners(prev => [...prev, _emptySigner()]);
    const removeSigner = (idx) =>
        setSigners(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

    const addFiles = (incoming) => {
        if (!incoming || incoming.length === 0) return;
        setPdfFiles(prev => [...prev, ...Array.from(incoming)]);
        // Reset the native input so re-selecting the same file still fires onChange.
        if (fileInputRef.current) fileInputRef.current.value = "";
    };
    const removeFile = (idx) => {
        setPdfFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const fileErrors = pdfFiles.map(_validateFile);
    const overCap = pdfFiles.length > MAX_FILES_SOFT_CAP;
    const hasAnyFileError = fileErrors.some(Boolean) || overCap;

    const submit = async () => {
        setErr("");
        if (pdfFiles.length === 0) {
            setErr("Please choose at least one PDF file.");
            return;
        }
        if (hasAnyFileError) {
            setErr("Fix the file errors highlighted below.");
            return;
        }
        if (!methodSummary.trim()) {
            setErr("Method summary is required.");
            return;
        }
        for (let i = 0; i < signers.length; i++) {
            const s = signers[i];
            if (!s.signer_name.trim()) { setErr(`Signer ${i + 1}: name is required.`); return; }
            if (!s.signer_role.trim()) { setErr(`Signer ${i + 1}: role is required.`); return; }
            if (!s.signed_on) { setErr(`Signer ${i + 1}: signed-on date is required.`); return; }
        }
        setBusy(true);
        try {
            const payload = signers.map(s => {
                const row = {
                    signer_name: s.signer_name.trim(),
                    signer_role: s.signer_role.trim(),
                    signed_on: s.signed_on,
                    signature_method: s.signature_method,
                };
                if (s.signer_email.trim()) row.signer_email = s.signer_email.trim();
                if (s.external_envelope_ref.trim()) row.external_envelope_ref = s.external_envelope_ref.trim();
                if (s.notes.trim()) row.notes = s.notes.trim();
                return row;
            });
            const fd = new FormData();
            pdfFiles.forEach(f => fd.append("pdfs", f));
            fd.append("signers", JSON.stringify(payload));
            fd.append("method_summary", methodSummary.trim());
            if (externalRef.trim()) fd.append("external_ref", externalRef.trim());
            fd.append("op_id", opId);
            const r = await axios.post(endpointUrl, fd, { withCredentials: true });
            if (onSuccessData) onSuccessData(r.data);
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail || e?.response?.data;
            setErr((d && (d.reason || d.message || d.error)) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-auto"
             data-testid={`${testIdPrefix}-modal`}>
            <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-6 my-10">
                <h2 className="font-manrope text-[15px] font-semibold mb-1">
                    {submitLabel}
                </h2>
                <p className="text-[11px] text-[#666] mb-4">{helpText}</p>

                <div className="mb-4">
                    <label className="block text-[12px] font-medium mb-1">
                        PDFs *
                        <span className="ml-2 font-normal text-[11px] text-[#666]">
                            25 MiB max per file; up to {MAX_FILES_SOFT_CAP} files per bundle.
                        </span>
                    </label>
                    <input ref={fileInputRef}
                           type="file" accept="application/pdf" multiple
                           data-testid={`${testIdPrefix}-pdf`}
                           onChange={e => addFiles(e.target.files)}
                           disabled={busy}
                           className="text-[12px] block mb-2" />

                    {pdfFiles.length > 0 && (
                        <div className="flex flex-col gap-1"
                             data-testid={`${testIdPrefix}-file-list`}>
                            {pdfFiles.map((f, i) => {
                                const fe = fileErrors[i];
                                return (
                                    <div key={`${f.name}-${f.size}-${f.lastModified}-${i}`}
                                         className={"flex items-center justify-between gap-3 "
                                             + "border rounded px-2 py-1 text-[12px] "
                                             + (fe ? "border-red-300 bg-red-50"
                                                   : "border-[#E5E2DC] bg-[#FCFBF9]")}
                                         data-testid={`${testIdPrefix}-file-row-${i}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate font-mono text-[11.5px]">
                                                {i + 1}. {f.name}
                                            </div>
                                            <div className="text-[10.5px] text-[#666]">
                                                {_fmtSize(f.size)}
                                                {fe && (
                                                    <span className="ml-2 text-red-700"
                                                          data-testid={`${testIdPrefix}-file-error-${i}`}>
                                                        {fe}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button type="button" onClick={() => removeFile(i)}
                                                disabled={busy}
                                                data-testid={`${testIdPrefix}-file-remove-${i}`}
                                                className="text-[11px] text-red-700 shrink-0">
                                            Remove
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-2 flex items-center gap-3">
                        <button type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={busy}
                                data-testid={`${testIdPrefix}-add-another`}
                                className="text-[11px] px-2 py-1 rounded bg-[#F5F3EE]">
                            + Add another file
                        </button>
                        {overCap && (
                            <span className="text-[11px] text-red-700"
                                  data-testid={`${testIdPrefix}-overcap`}>
                                Too many files — remove some; soft cap {MAX_FILES_SOFT_CAP}.
                            </span>
                        )}
                    </div>
                </div>

                <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-[12px] font-medium">Signers *</label>
                        <button type="button" onClick={addSigner}
                                data-testid={`${testIdPrefix}-add-signer`}
                                className="text-[11px] px-2 py-1 rounded bg-[#F5F3EE]">
                            + Add signer
                        </button>
                    </div>
                    <div className="flex flex-col gap-3">
                        {signers.map((s, i) => (
                            <div key={i}
                                 data-testid={`${testIdPrefix}-signer-${i}`}
                                 className="border border-[#E5E2DC] rounded p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] uppercase text-[#666]">Signer {i + 1}</span>
                                    {signers.length > 1 && (
                                        <button type="button" onClick={() => removeSigner(i)}
                                                data-testid={`${testIdPrefix}-remove-signer-${i}`}
                                                className="text-[11px] text-red-700">
                                            Remove
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-[12px]">
                                    <label className="flex flex-col gap-1">Name *
                                        <input value={s.signer_name}
                                               onChange={e => updateSigner(i, { signer_name: e.target.value })}
                                               data-testid={`${testIdPrefix}-signer-${i}-name`}
                                               className="border rounded px-2 py-1 text-[12px]" />
                                    </label>
                                    <label className="flex flex-col gap-1">Email
                                        <input type="email" value={s.signer_email}
                                               onChange={e => updateSigner(i, { signer_email: e.target.value })}
                                               data-testid={`${testIdPrefix}-signer-${i}-email`}
                                               className="border rounded px-2 py-1 text-[12px]" />
                                    </label>
                                    <label className="flex flex-col gap-1">Role *
                                        <input value={s.signer_role}
                                               placeholder="e.g. Client, Co-Client, Contractor Principal"
                                               onChange={e => updateSigner(i, { signer_role: e.target.value })}
                                               data-testid={`${testIdPrefix}-signer-${i}-role`}
                                               className="border rounded px-2 py-1 text-[12px]" />
                                    </label>
                                    <label className="flex flex-col gap-1">Signed on *
                                        <input type="date" value={s.signed_on}
                                               onChange={e => updateSigner(i, { signed_on: e.target.value })}
                                               data-testid={`${testIdPrefix}-signer-${i}-signed-on`}
                                               className="border rounded px-2 py-1 text-[12px]" />
                                    </label>
                                    <label className="flex flex-col gap-1">Method *
                                        <select value={s.signature_method}
                                                onChange={e => updateSigner(i, { signature_method: e.target.value })}
                                                data-testid={`${testIdPrefix}-signer-${i}-method`}
                                                className="border rounded px-2 py-1 text-[12px]">
                                            {Object.entries(EXT_METHOD_LABELS).map(([v, lbl]) => (
                                                <option key={v} value={v}>{lbl}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="flex flex-col gap-1">External envelope ref
                                        <input value={s.external_envelope_ref}
                                               onChange={e => updateSigner(i, { external_envelope_ref: e.target.value })}
                                               data-testid={`${testIdPrefix}-signer-${i}-env-ref`}
                                               className="border rounded px-2 py-1 text-[12px]" />
                                    </label>
                                    <label className="col-span-2 flex flex-col gap-1">Notes
                                        <textarea rows={2} value={s.notes}
                                                  onChange={e => updateSigner(i, { notes: e.target.value })}
                                                  data-testid={`${testIdPrefix}-signer-${i}-notes`}
                                                  className="border rounded px-2 py-1 text-[12px]" />
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <label className="block text-[12px] font-medium mb-1">Method summary *</label>
                <textarea rows={2} value={methodSummary}
                          onChange={e => setMethodSummary(e.target.value)}
                          data-testid={`${testIdPrefix}-method-summary`}
                          placeholder="e.g. Signed via Zoho Sign envelope ZS-123 on 2026-01-15 by all parties"
                          className="w-full border rounded px-2 py-1 text-[12px] mb-4" />

                <label className="block text-[12px] font-medium mb-1">External reference</label>
                <input value={externalRef}
                       onChange={e => setExternalRef(e.target.value)}
                       data-testid={`${testIdPrefix}-external-ref`}
                       className="w-full border rounded px-2 py-1 text-[12px] mb-4" />

                {err && (
                    <div data-testid={`${testIdPrefix}-error`}
                         className="mb-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
                        {err}
                    </div>
                )}

                <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[#666]">
                        op_id: <code>{opId.slice(0, 18)}…</code>
                    </span>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} disabled={busy}
                                data-testid={`${testIdPrefix}-cancel`}
                                className="text-[12px] px-3 py-1.5">
                            Cancel
                        </button>
                        <button type="button" onClick={submit}
                                disabled={busy || pdfFiles.length === 0 || hasAnyFileError}
                                data-testid={`${testIdPrefix}-submit`}
                                className="text-[12px] px-3 py-1.5 rounded bg-[#A8543F] text-white disabled:opacity-60">
                            {busy ? "Uploading…" : submitLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>);
}
